import {
  Box,
  Flex,
  Skeleton,
  Table,
  Tbody,
  Th,
  Thead,
  Tr,
  Td,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch, useFormState } from 'react-hook-form';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';

import {
  FundingRequestEntitiesFragmentGQLType,
  FundingLenderSharesFragmentGQLType,
} from '../../../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { DateInput, InputContainer, TextInput } from '../../Inputs';
import { EditLoanFormFields, FundingRequestComponentsState } from '../../types';
import { useEditLoanContext } from '../../useEditLoanContext';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';

type FundingRequestFormProps = {
  fundingRequestId: string;
  readOnly?: boolean;
};

const ErrorAlert = ({ message }: { message: any }) => {
  if (typeof message !== 'string') return null;
  return (
    <Alert status="error">
      <AlertIcon />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
};

const FundingRequestEditFormInner = ({
  fundingRequestId,
  readOnly = false,
}: FundingRequestFormProps) => {
  const context = useEditLoanContext();
  const {
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    resetField,
    trigger,
  } = useFormContext();
  const formState = useFormState<EditLoanFormFields>();
  const fundingRequestComponents = useWatch<
    Record<string, FundingRequestComponentsState>
  >({
    control,
    name: `fundingRequests.${fundingRequestId}.components`,
  });

  useEffect(() => {
    if (!fundingRequestComponents) return;

    const globalRequestAmount = Number(
      getValues(`fundingRequests.${fundingRequestId}.totalAmount`),
    );

    const componentsSum = Object.values<{
      amount: number;
    }>(fundingRequestComponents).reduce(
      (acc, { amount }) =>
        // Inputs should only be 2 decimal places anyways but using 4 here
        // due to how JS manages float math
        commonUtils.jsRound({ value: acc + Number(amount), precision: 4 }),
      0,
    );

    if (globalRequestAmount !== componentsSum) {
      setError(`fundingRequests.${fundingRequestId}.components`, {
        message: `The total of ${formatCurrencyAmount(
          componentsSum,
          Bank.Currency.USD,
        )} does not match the draw request amount of ${formatCurrencyAmount(
          globalRequestAmount,
          Bank.Currency.USD,
        )}. Revise accordingly.`,
      });
    } else {
      clearErrors(`fundingRequests.${fundingRequestId}.components`);
    }
  }, [
    fundingRequestId,
    fundingRequestComponents,
    getValues,
    setError,
    clearErrors,
  ]);

  const handleGlobalDrawUpdate = useCallback(() => {
    if (context.status !== 'ready') return;

    const globalRequestAmount = Number(
      getValues(`fundingRequests.${fundingRequestId}.totalAmount`),
    );

    const { lenderShares } = context.state.originalResolvedLoan.funding;
    const loanFundingData = context.state.originalResolvedLoan.funding.requests.find(
      (request) => request.fundingRequestId === fundingRequestId,
    );

    finleyAssert(
      loanFundingData,
      `Funding request [id:${fundingRequestId}] not found when allocating total draw amount`,
    );

    // Adding up each of the component amounts first before setting them to help manage
    // any potential rounding remainders, which we'll just allocate to the first component
    let totalAmount = 0;
    const fundingComponentAmountMap: Record<string, number> = {};
    for (const fundingComponent of loanFundingData.fundingComponents) {
      const { fundingRequestComponentId, lenderShareId } = fundingComponent;
      const lenderShare = lenderShares.find(
        (currentLenderShare) =>
          currentLenderShare.lenderShareId === lenderShareId,
      );
      finleyAssert(
        lenderShare,
        `Lender share [id: ${lenderShareId}] not found`,
      );

      const lenderShareAmount = commonUtils.jsRound({
        value: lenderShare.allocationPercent * globalRequestAmount,
        precision: 2,
      });

      totalAmount = commonUtils.jsRound({
        value: totalAmount + lenderShareAmount,
        precision: 2,
      });
      fundingComponentAmountMap[fundingRequestComponentId] = lenderShareAmount;
    }

    let remainingAmount = commonUtils.jsRound({
      value: globalRequestAmount - totalAmount,
      precision: 2,
    });

    for (const entry of Object.entries(fundingComponentAmountMap)) {
      const [fundingRequestComponentId, baseAmount] = entry;

      // This will just be adding the remaining amount (if any) to the
      // first component. If present, should likely just be a cent or so
      let amount = baseAmount;
      if (remainingAmount !== 0) {
        amount = commonUtils.jsRound({
          value: amount + remainingAmount,
          precision: 2,
        });

        remainingAmount = 0;
      }
      setValue(
        `fundingRequests.${fundingRequestId}.components.${fundingRequestComponentId}.amount`,
        amount,
        { shouldDirty: true },
      );
    }

    // Want to trigger after all the values are set so validation is done all at once.
    // Can't do validation at each setValue since the total would be inaccurate since
    // the sum would only work after all values are set
    trigger(`fundingRequests.${fundingRequestId}.components`);
  }, [
    context.status,
    context.state?.originalResolvedLoan.funding,
    getValues,
    fundingRequestId,
    trigger,
    setValue,
  ]);

  const loanFundingData = useMemo<{
    fundingRequest: FundingRequestEntitiesFragmentGQLType;
    lenderShares: FundingLenderSharesFragmentGQLType[];
  } | null>(() => {
    if (context.status !== 'ready') return null;

    const { newEntities, originalResolvedLoan } = context.state;

    if (newEntities) {
      const { lenderShares, requests } = newEntities.newResolvedLoan.funding;

      const fundingRequest = requests.find(
        (request) => request.fundingRequestId === fundingRequestId,
      );
      finleyAssert(
        fundingRequest,
        `Funding request [id: ${fundingRequestId}] not found`,
      );

      return { fundingRequest, lenderShares };
    }

    const { lenderShares, requests } = originalResolvedLoan.funding;

    const fundingRequest = requests.find(
      (request) => request.fundingRequestId === fundingRequestId,
    );
    finleyAssert(
      fundingRequest,
      `Funding request [id: ${fundingRequestId}] not found`,
    );

    return { fundingRequest, lenderShares };
  }, [context.state, context.status, fundingRequestId]);

  if (context.status !== 'ready' || !loanFundingData) {
    // TODO: add loading state
    return <Skeleton w="100%" h="10rem" />;
  }

  const { fundingRequest, lenderShares } = loanFundingData;

  return (
    <Flex width="100%" gap="1rem" alignItems="flex-start" direction="column">
      <InputContainer label="Draw Request Amount">
        <TextInput
          name={`fundingRequests.${fundingRequestId}.totalAmount`}
          placeholder="$0.00"
          mask="usd"
          onReset={() => {
            Object.values(fundingRequestComponents).forEach(({ id }) => {
              resetField(
                `fundingRequests.${fundingRequestId}.components.${id}.amount`,
                {
                  keepDirty: false,
                  keepError: false,
                  keepTouched: false,
                },
              );
            });
          }}
          rules={{
            required: true,
            valueAsNumber: true,
            onBlur: handleGlobalDrawUpdate,
          }}
          readOnly={readOnly}
        />
      </InputContainer>

      <InputContainer label="Funding Date">
        <Box w="100%">
          <DateInput
            name={`fundingRequests.${fundingRequestId}.targetFundingDate`}
            rules={{ required: true }}
            readOnly={readOnly}
            dateBoxProps={{ w: '100%' }}
          />
          <Typography variant="detail-muted">
            Past funding date will act as settlement date
          </Typography>
        </Box>
      </InputContainer>

      <Table>
        <Thead>
          <Tr>
            <Th>Lender</Th>
            <Th>Fund Transfer Date</Th>
            <Th>Share</Th>
            <Th>Principal</Th>
          </Tr>
        </Thead>
        <Tbody>
          {fundingRequest.fundingComponents.map(
            ({ fundingRequestComponentId, lenderShareId }) => {
              const lenderShare = lenderShares.find(
                (ls) => ls.lenderShareId === lenderShareId,
              );
              finleyAssert(
                lenderShare,
                `Lender share [id: ${lenderShareId}] not found`,
              );

              return (
                <Tr key={fundingRequestComponentId}>
                  <Td>{lenderShare.lenderOrganization.name}</Td>
                  <Td w="320px">
                    <DateInput
                      name={`fundingRequests.${fundingRequestId}.components.${fundingRequestComponentId}.fundTransferDate`}
                      rules={{ required: false }}
                      minDate={getValues(
                        `fundingRequests.${fundingRequestId}.targetFundingDate`,
                      )}
                      isSelectedDateOptional
                      readOnly={readOnly}
                      dateBoxProps={{ w: '2xs' }}
                    />
                  </Td>
                  <Td w="200px">
                    {percentageToPercentageLabel(
                      commonUtils.divideByOrZero(
                        fundingRequestComponents[fundingRequestComponentId]
                          .amount,
                        getValues(
                          `fundingRequests.${fundingRequestId}.totalAmount`,
                        ),
                      ) * 100,
                      2,
                      true,
                    )}
                  </Td>
                  <Td w="lg">
                    <TextInput
                      name={`fundingRequests.${fundingRequestId}.components.${fundingRequestComponentId}.amount`}
                      placeholder="$0.00"
                      mask="usd"
                      readOnly={readOnly}
                      showErrorMessage={false}
                      rules={{
                        validate: {
                          invalidComponentsSum: (_, { fundingRequests }) => {
                            const fundingRequestValues =
                              fundingRequests[fundingRequestId];
                            finleyAssert(
                              fundingRequestValues,
                              `Funding request [id:${fundingRequestId}] not found when validating [component:${fundingRequestComponentId}]`,
                            );

                            const nonRoundedSum = Object.values<{
                              amount: number;
                            }>(fundingRequestValues.components).reduce(
                              (acc, { amount }) => acc + amount,
                              0,
                            );

                            const componentsSum = commonUtils.jsRound({
                              value: nonRoundedSum,
                              precision: 2,
                            });

                            const roundedTotal = commonUtils.jsRound({
                              value: fundingRequestValues.totalAmount,
                              precision: 2,
                            });

                            return roundedTotal === componentsSum || '';
                          },
                        },
                        deps: [
                          `fundingRequests.${fundingRequestId}.totalAmount`,
                          `fundingRequests.${fundingRequestId}.components`,
                        ],
                      }}
                    />
                  </Td>
                </Tr>
              );
            },
          )}
        </Tbody>
      </Table>

      <ErrorAlert
        message={
          formState.errors?.fundingRequests?.[fundingRequestId]?.components
            ?.message
        }
      />
    </Flex>
  );
};

/**
 * Need to memoize this form to force re-render on prop changes
 * since underlying watch/useWatch from react-hook-form does not
 * trigger re-render on prop changes, causing errors when navigating
 * from one funding request form to another
 */
export const FundingRequestEditForm = ({
  fundingRequestId,
  readOnly,
}: FundingRequestFormProps) => {
  return (
    <FundingRequestEditFormInner
      key={fundingRequestId}
      fundingRequestId={fundingRequestId}
      readOnly={readOnly}
    />
  );
};
